import _ from "lodash";
import React, { useRef, useMemo } from "react";
import classes from "./StudentRoutineDetails.scss";

import { Tabs, Avatar, Tooltip, Badge } from "@toddle-edu/ds-web";

import { AVATAR_COLORS } from "Constants/colorConstants";
import { hashString } from "Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getFormattedUserNameWithoutPronoun } from "Post/utils";
import ScrollableCarousel from "UIComponents/ScrollableCarousel";
import FieldValue from "../RoutinePostCard/RoutineDetailCard/FieldValue/FieldValue";

const wrapperStyle = {
  position: "sticky",
  top: "0px",
  background: "var(--surface-primary-enabled)",
  zIndex: "10",
};
const scrollableContainerStyle = {
  ...wrapperStyle,
  paddingTop: "16px",
  marginTop: "-24px",
};
const StudentRoutineDetails = props => {
  const {
    defaultStudentNameOrderPreference,
    setTheatreAttachmentIndex,
    theatreActiveAttachmentIndex,
    isSidePaneContent,
    taggedStudents,
    selectedRoutineStudentId,
    filteredStudentRoutines,
    handleActivitySelectedStudentIdChange,
    handleActivitySelectedStudentAttachmentsChange,
    fromPropsHandleActivitySelectedStudentChange,
    fromPropsHandleActivitySelectedStudentAttachmentsChange,
    userType,
    studentId,
    hasAttachments,
    postTemplateFields,
  } = props;
  const initialStudentId = selectedRoutineStudentId;
  const scrollableContainerRef = useRef(null);
  const studentItem = useMemo(() => {
    return _.find(
      filteredStudentRoutines,
      item => item?.item?.routineStudent?.id === selectedRoutineStudentId
    );
  }, [filteredStudentRoutines, selectedRoutineStudentId]);
  const getScrollableContainerRef = ref =>
    (scrollableContainerRef.current = ref?.current);

  const studentsTagged = useMemo(() => {
    if (userType === "staff") {
      return taggedStudents;
    } else {
      return _.filter(
        taggedStudents,
        student => _.get(student, "node.id") === studentId
      );
    }
  }, [taggedStudents]);

  const tabs = _.map(studentsTagged, ({ node: activityData }) => {
    const id = _.get(activityData, "id", "");
    const firstName = _.get(activityData, "firstName", "");
    const lastName = _.get(activityData, "lastName", "");
    const profileImage = _.get(activityData, "profileImage", "");

    const studentName = getFormattedUserName({
      user: activityData,
    });
    const studentNameForAvatar = getFormattedUserNameWithoutPronoun({
      user: activityData,
    });

    return {
      value: `routineDetail:${id}`,
      suffix: (
        <Tooltip tooltip={studentName}>
          <span className={classes.tabLabel}>{studentName}</span>
        </Tooltip>
      ),
      customComponent: (
        <ConditionalWrapper
          condition={
            _.get(activityData, ["item", "comments", "unreadCountV2"], 0) > 0
          }
          wrapper={
            <Badge
              type="dot"
              size="large"
              dotBadgeColorVariant="critical"
              offset={[4, 4]}
            />
          }
          type={"wrapperAsElement"}
        >
          <Avatar
            shade={"light"}
            size={"small"}
            color={AVATAR_COLORS[hashString(id) % AVATAR_COLORS.length]}
            name={
              _.size(defaultStudentNameOrderPreference) === 3
                ? `${firstName} ${lastName}`
                : studentNameForAvatar
            }
            src={profileImage}
          />
        </ConditionalWrapper>
      ),
    };
  });

  const getSelectedStudentAttachments = _.memoize(
    selectStudentAttachmentData => {
      let allAttachments = [];

      if (!_.isNil(selectStudentAttachmentData)) {
        const attachmentsGroups = _.get(
          selectStudentAttachmentData,
          "attachmentGroups",
          []
        );
        _.forEach(attachmentsGroups, group => {
          const attachments = _.get(group, "attachments", []);
          allAttachments = _.concat(allAttachments, attachments);
        });
      }
      return allAttachments;
    },
    selectStudentAttachmentData => {
      const attachmentGroups = _.get(
        selectStudentAttachmentData,
        "attachmentGroups",
        []
      );
      return _.map(
        attachmentGroups,
        group => `${group.id}_${_.map(group.attachments, "id").join(",")}`
      ).join("|");
    }
  );
  const handleStudentTabChange = value => {
    const id = _.get(_.split(value, ":"), "1", "");
    const nextSelectedStudentRoutineData = _.get(
      _.find(
        filteredStudentRoutines,
        activity => _.get(activity, "item.routineStudent.id") === id
      ),
      "item",
      {}
    );

    const attachmentObjectFields = postTemplateFields;

    const attachmentFieldId = _.filter(attachmentObjectFields, {
      type: "ATTACHMENT",
    });
    const attachmentValue = _.filter(
      _.get(nextSelectedStudentRoutineData, "fields"),
      item => _.includes(_.map(attachmentFieldId, "id"), item.uid)
    );
    if (_.isFunction(fromPropsHandleActivitySelectedStudentAttachmentsChange)) {
      const updatedAttacments = [];
      _.forEach(attachmentValue, attachmentObj => {
        updatedAttacments.push(
          ...(getSelectedStudentAttachments(
            _.get(attachmentObj, "resolvedMinimalTree")
          ) || [])
        );
      });
      fromPropsHandleActivitySelectedStudentAttachmentsChange(
        setTheatreAttachmentIndex,
        updatedAttacments
      );
    }

    if (_.isFunction(setTheatreAttachmentIndex)) setTheatreAttachmentIndex(0);
    handleActivitySelectedStudentIdChange(id);
  };
  const onTheatreAttachmentClick = index => e => {
    setTheatreAttachmentIndex?.(index);
  };
  return (
    <>
      {" "}
      {_.size(tabs) > 1 ? (
        <ScrollableCarousel
          getScrollableContainerRef={getScrollableContainerRef}
          scrollAmount={89 * 3}
          chevronIconSize={"xxx-small"}
          wrapperStyle={
            !hasAttachments ? scrollableContainerStyle : wrapperStyle
          }
        >
          <div className={classes.tabContainer}>
            <Tabs
              options={tabs}
              value={`routineDetail:${initialStudentId}`}
              onChange={handleStudentTabChange}
              type={"flexible"}
              containerStyle={{ height: "36px", width: "76px" }}
              testId="journal-feed-student-routines-tabs"
            />
          </div>
        </ScrollableCarousel>
      ) : null}
      <FieldValue
        isSidePaneContent={isSidePaneContent}
        fieldValues={_.get(studentItem, "item.fields")}
        fields={postTemplateFields}
        theatreActiveAttachmentIndex={theatreActiveAttachmentIndex}
        setTheatreAttachmentIndex={setTheatreAttachmentIndex}
        onTheatreAttachmentClick={onTheatreAttachmentClick}
      />
    </>
  );
};

export default StudentRoutineDetails;
